import { Token } from '@stripe/stripe-js';
import {
  AdminHeader,
  AdminSpinner,
  Div,
  ErrorToast,
  H5,
  MotionDiv,
  SuccessToast,
  handleSetUrlSearchParams,
  startVerificationProcessForLocation,
  reportError,
  VDrivePaymentMode,
} from '@vgrubs/components';
import { LocationVDriveEntity } from 'components/src/types/location.types';
import { ResponseType } from 'components/src/types/shared.types';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { AddCardVDrive } from './AddCard';
import { AlmostReady } from './AlmostReady';
import { GetStartedVDrive } from './GetStartedVDrive';
import { SetAutoRecharge, autoRechargeAmounts } from './SetAutoRecharge';

export const VDriveLocationOnboarding = () => {
  const [loading, setLoading] = useState(false);

  const { id } = useParams() as { id: string };

  // A rejected vDrive lands here from Manage vDrive Location with the
  // response in the route state; the reason is gone once it starts again.
  const rejectionReason = (
    useLocation().state as ResponseType<LocationVDriveEntity> | null
  )?.data?.rejectionReason;

  const [searchParams, setSearchParams] = useSearchParams();

  const step = Number(searchParams.get('step'));

  const [stripeToken, setStripeToken] = useState<Token | undefined>(undefined);

  // const [amount, setAmount] = useState(5000);

  const [autoRechargeAmount, setAutoRechargeAmount] = useState(
    autoRechargeAmounts[1].value
  );
  const [autoRechargeWhenAmount, setAutoRechargeWhenAmount] = useState<
    number | null
  >(autoRechargeAmounts[0].value);

  const [paymentMode, setPaymentMode] = useState(VDrivePaymentMode.perDelivery);

  async function handleSubmitVDrive() {
    if (!stripeToken) {
      ErrorToast('Please add your card to proceed');
      setStep(1);
      return;
    }

    // if (amount < 2500) {
    //   ErrorToast('Minimum amount is $25');
    //   setStep(2);
    //   return;
    // }

    const body = {
      stripeToken: stripeToken?.id,
      paymentMode,
      // Money up front only for a prepaid balance, where it is required.
      amount: paymentMode === VDrivePaymentMode.topUp ? 2500 : undefined,
      // Only the owner's own card from here on - the vGrubs vDrive card is
      // being retired and the backend refuses to assign it.
      isVDriveCard: false as const,
      // Sent either way: per delivery keeps them for a switch to top-up.
      autoRechargeAmount: Number(autoRechargeAmount),
      autoRechargeWhenAmount: Number(autoRechargeWhenAmount),
    };

    try {
      setLoading(true);
      const { data } = await startVerificationProcessForLocation(id, body);
      SuccessToast(data.message);
      setStep(3);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const setStep = (step) => {
    handleSetUrlSearchParams('step', step, setSearchParams);
  };

  const variants = {
    open: {
      opacity: 1,
      transition: {
        duration: 0.3,
        type: 'ease',
      },
      display: 'block',
    },
    closed: {
      opacity: 0,
      transition: {
        duration: 0.2,
        type: 'ease',
      },
      display: 'none',
    },
  };

  return (
    <>
      <AdminHeader title={'vDrive Onboarding'}>
        {step > 0 && step < 3 && (
          <H5 $m="0" $right>
            <span>Step {step}/2</span>
          </H5>
        )}
      </AdminHeader>
      {loading && <AdminSpinner />}

      <Div $flex $center $minHeight="calc(100vh - 150px)">
        <Div $maxWidth="400px" $width="100%">
          <AnimatePresence mode="wait">
            <MotionDiv
              key={0}
              animate={step === 0 ? 'open' : 'closed'}
              variants={variants}
            >
              <GetStartedVDrive
                setStep={setStep}
                rejectionReason={rejectionReason}
              />
            </MotionDiv>
            <MotionDiv
              key={1}
              animate={step === 1 ? 'open' : 'closed'}
              variants={variants}
            >
              <AddCardVDrive
                setStripeToken={setStripeToken}
                setStep={setStep}
              />
            </MotionDiv>

            <MotionDiv
              key={2}
              animate={step === 2 ? 'open' : 'closed'}
              variants={variants}
            >
              <SetAutoRecharge
                paymentMode={paymentMode}
                setPaymentMode={setPaymentMode}
                autoRechargeAmount={autoRechargeAmount}
                setAutoRechargeAmount={setAutoRechargeAmount}
                autoRechargeWhenAmount={autoRechargeWhenAmount}
                setAutoRechargeWhenAmount={setAutoRechargeWhenAmount}
                handleSubmitVDrive={handleSubmitVDrive}
              />
            </MotionDiv>
            <MotionDiv
              key={3}
              animate={step === 3 ? 'open' : 'closed'}
              variants={variants}
            >
              <AlmostReady />
            </MotionDiv>
          </AnimatePresence>
        </Div>
      </Div>
    </>
  );
};
